import React, { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Booking() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    classType: 'group',
    date: '',
    participants: '1',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d66a5umnnp6hevao8gb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          classType: 'group',
          date: '',
          participants: '1',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="booking" className="py-32 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-50/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-flex items-center gap-3 px-6 py-2.5 bg-amber-500/10 backdrop-blur-sm rounded-full border border-amber-500/20 mb-6">
            <i className="ri-calendar-check-line text-amber-400"></i>
            <span className="text-amber-300 text-sm font-medium tracking-widest uppercase">Reserve Your Spot</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-5">
            Book Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400">Experience</span>
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto text-base leading-relaxed mb-6">
            Ready to start your mixology journey? Fill out the form below and we&apos;ll get back to you within 24 hours.
          </p>
          <p className="text-amber-400/70 text-sm max-w-xl mx-auto">
            💳 For instant booking with payment, use the &quot;Book Now&quot; buttons on our classes and packages above.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-20">
          {/* Left Content */}
          <div 
            ref={leftRef}
            className={`scroll-animate-left ${leftVisible ? 'visible' : ''}`}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-amber-500 to-transparent"></div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">Reserve Your Spot</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-display font-normal text-stone-900 mb-6 leading-tight">
              Book Your
              <br />
              <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-400">Experience</span>
            </h2>
            
            <p className="text-stone-500 mb-10 leading-relaxed text-lg">
              Ready to master the art of mixology? Book your class now and start your journey to
              becoming a cocktail expert.
            </p>

            {/* Features */}
            <div className={`space-y-6 mb-12 stagger-children ${leftVisible ? 'visible' : ''}`}>
              {[
                { icon: 'ri-calendar-check-line', title: 'Flexible Scheduling', desc: 'Weekday, weekend, or evening classes' },
                { icon: 'ri-group-line', title: 'Small Class Sizes', desc: 'Maximum 8 students for personalized attention' },
                { icon: 'ri-award-line', title: 'Certificate Included', desc: 'Professional certificate upon completion' },
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-5 group">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-300 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20 group-hover:scale-110 transition-transform">
                    <i className={`${feature.icon} text-xl text-stone-900`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-1">{feature.title}</h4>
                    <p className="text-stone-500 text-sm">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Card */}
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-stone-100">
              <h4 className="font-serif text-xl text-stone-900 mb-6">Contact Information</h4>
              <div className="space-y-4">
                <a
                  href="mailto:hello@houseoffmr.com"
                  className="flex items-center gap-4 text-stone-600 hover:text-amber-600 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <i className="ri-mail-line text-amber-600"></i>
                  </div>
                  hello@houseoffmr.com
                </a>
                <a
                  href="tel:0411164144"
                  className="flex items-center gap-4 text-stone-600 hover:text-amber-600 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <i className="ri-phone-line text-amber-600"></i>
                  </div>
                  0411 164 144
                </a>
                <div className="flex items-center gap-4 text-stone-600">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
                    <i className="ri-map-pin-line text-amber-600"></i>
                  </div>
                  123 Cocktail Lane, London, UK
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div 
            ref={formRef}
            className={`relative scroll-animate-right ${formVisible ? 'visible' : ''}`}
          >
            <div className="absolute -inset-4 border border-amber-200/50 rounded-[2.5rem] hidden lg:block"></div>
            
            <div className="bg-white rounded-3xl p-10 shadow-2xl border border-stone-100 relative">
              <form
                id="booking-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50"
                      placeholder="+1 (234) 567-890"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Experience Type *
                    </label>
                    <select
                      name="classType"
                      value={formData.classType}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50 cursor-pointer"
                    >
                      <option value="group">Group Class ($149)</option>
                      <option value="advanced">Advanced Mixology ($249)</option>
                      <option value="private">Private One-on-One ($349)</option>
                      <option value="hens">Hen&apos;s Party Experience ($129/person)</option>
                      <option value="couples">Home Bar Service for Couples ($399)</option>
                      <option value="corporate">Corporate Event ($89/person)</option>
                      <option value="pkg-starter">Package: The Starter Sparkle ($219)</option>
                      <option value="pkg-golden">Package: The Golden Hour ($389)</option>
                      <option value="pkg-diamond">Package: The Diamond Experience ($699)</option>
                      <option value="pkg-hens">Package: Hen&apos;s Party Glam ($329)</option>
                      <option value="pkg-couples">Package: Couples Date Night Luxe ($449)</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                      Participants
                    </label>
                    <select
                      name="participants"
                      value={formData.participants}
                      onChange={handleChange}
                      className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm bg-stone-50/50 cursor-pointer"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, '9+'].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'person' : 'people'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                    Special Requests
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-5 py-4 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-sm resize-none bg-stone-50/50"
                    placeholder="Any dietary requirements, special occasions, or questions?"
                  ></textarea>
                  <p className="text-xs text-stone-400 mt-2">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-stone-900 font-semibold rounded-xl hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-amber-500/30 text-sm tracking-wide"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line text-lg"></i>
                      Request Booking
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-double-line text-emerald-600 text-lg"></i>
                    </div>
                    <p className="text-emerald-700 text-sm">
                      Thank you! We&apos;ll confirm your booking within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-5 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-rose-600 text-lg"></i>
                    </div>
                    <p className="text-rose-700 text-sm">
                      Something went wrong. Please try again or contact us directly.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
